import PageMeta from "@/components/seo/PageMeta";
import JsonLd from "@/components/seo/JsonLd";
import HeroSection from "@/components/sections/HeroSection";
import EditorialBlock from "@/components/sections/EditorialBlock";
import EditorialImageCollage from "@/components/sections/EditorialImageCollage";
import Divider from "@/components/ui/Divider";
import ScriptText from "@/components/ui/ScriptText";
import Button from "@/components/ui/Button";
import { homeContent, resolveImage } from "@/data/content";
import { useContent } from "@/hooks/useContent";

export default function HomePage() {
  const content = useContent("pages/home", homeContent);
  const { hero, mission, about, village, intimate, brandPromise, cta } = content;

  return (
    <>
      <PageMeta
        title="Intimate Women's Retreats in Provence, Tuscany & the Cotswolds | Chasing Provence"
        description="Join intimate luxury women's retreats in Provence, Tuscany, and the Cotswolds. Slow living, local cuisine, and curated experiences in Europe's most beautiful regions."
        canonicalPath="/"
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Chasing Provence",
          url: "https://chasingprovence.com",
        }}
      />

      <HeroSection
        heading={hero.heading}
        subheading={hero.subheading}
        cta={hero.cta}
        ctaLink={hero.ctaLink}
        backgroundImage={resolveImage("/images/home/", (hero as { backgroundImage?: string }).backgroundImage)}
      />

      <EditorialBlock
        scriptAccent={mission.scriptAccent}
        heading={mission.heading}
        body={mission.body}
      />

      <Divider />

      <EditorialImageCollage
        scriptAccent={about.scriptAccent}
        heading={about.heading}
        body={about.body}
        images={about.images.map((img: { image?: string; alt: string }) => ({
          src: resolveImage("/images/home/", img.image),
          alt: img.alt,
        }))}
        imagePosition="right"
      />

      <Divider />

      <EditorialImageCollage
        scriptAccent={village.scriptAccent}
        heading={village.heading}
        body={village.body}
        images={village.images.map((img: { image?: string; alt: string }) => ({
          src: resolveImage("/images/home/", img.image),
          alt: img.alt,
        }))}
        imagePosition="left"
      />

      {/* By Design — closing section with promise & CTAs */}
      <section className="px-6 py-28 lg:px-12 lg:py-44">
        <div className="mx-auto max-w-3xl text-center">
          <ScriptText className="mb-3 block text-3xl lg:text-4xl">
            {intimate.scriptAccent}
          </ScriptText>
          <h2 className="mb-12 font-serif font-light tracking-wide text-soft-charcoal">
            {intimate.heading}
          </h2>
          <p className="mx-auto mb-14 max-w-2xl font-body text-lg leading-[1.9] text-soft-charcoal/80 lg:text-xl">
            {intimate.body}
          </p>
          <blockquote className="mx-auto mb-14 max-w-2xl border-y border-warm-stone/20 py-10 font-serif text-xl font-light leading-relaxed text-soft-charcoal/90 lg:text-2xl lg:leading-snug">
            {brandPromise.text}
          </blockquote>
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center sm:gap-6">
            <Button to={cta.primaryButtonLink} variant="filled">
              {cta.primaryButtonText}
            </Button>
            <Button to={cta.secondaryButtonLink} variant="outlined">
              {cta.secondaryButtonText}
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
